import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const SYMS = ["NIFTY", "BANKNIFTY", "SENSEX"];
const istT = (iso) => new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

export const QLMarketData = () => {
  const [sym, setSym] = useState("NIFTY");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [candles, setCandles] = useState(null);
  const [contracts, setContracts] = useState(null);
  const [cid, setCid] = useState("");
  const [optCandles, setOptCandles] = useState(null);

  const loadCandles = () => {
    const p = new URLSearchParams({ symbol: sym, limit: "500" });
    if (from) p.set("date_from", from);
    if (to) p.set("date_to", to);
    api.get(`/quantlab/candles?${p}`).then((r) => setCandles(r.data)).catch(() => {});
  };
  useEffect(() => {
    loadCandles();
    api.get(`/quantlab/contracts?symbol=${sym}`).then((r) => setContracts(r.data)).catch(() => {});
    setCid(""); setOptCandles(null);
  }, [sym]); // eslint-disable-line

  useEffect(() => {
    if (!cid) return;
    api.get(`/quantlab/option-candles?contract_id=${encodeURIComponent(cid)}&limit=500`)
      .then((r) => setOptCandles(r.data)).catch(() => {});
  }, [cid]);

  const rows = candles?.items || [];
  const shown = rows.slice(-120).reverse();

  return (
    <div className="space-y-5" data-testid="ql-market-data">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex gap-1">
          {SYMS.map((s) => (
            <button key={s} onClick={() => setSym(s)}
                    className={`px-3 py-2 text-[11px] font-mono border ${sym === s ? "border-[#22C55E] text-white" : "border-zinc-800 text-zinc-500 hover:text-white"}`}
                    data-testid={`ql-md-sym-${s}`}>{s}</button>
          ))}
        </div>
        <div className="flex items-end gap-2 text-[10px] font-mono text-zinc-500">
          <div><div className="mb-1">FROM</div>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="bg-black border border-zinc-800 px-2 py-1.5 text-white" data-testid="ql-md-from" /></div>
          <div><div className="mb-1">TO</div>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="bg-black border border-zinc-800 px-2 py-1.5 text-white" data-testid="ql-md-to" /></div>
          <button onClick={loadCandles} className="border border-zinc-700 px-3 py-1.5 text-zinc-300 hover:text-white uppercase tracking-widest" data-testid="ql-md-apply">Apply</button>
        </div>
        <div className="ml-auto text-[10px] font-mono text-zinc-500">{candles ? `${candles.count} candles loaded (5m · IST)` : ""}</div>
      </div>

      <div className="border border-zinc-800 bg-[#0a0a0a] overflow-x-auto">
        <table className="w-full text-xs font-mono">
          <thead><tr className="text-zinc-500 text-[10px] uppercase border-b border-zinc-900">
            {["Time (IST)", "Open", "High", "Low", "Close", "Volume", "Source"].map((h) => <th key={h} className="px-3 py-2 text-left">{h}</th>)}
          </tr></thead>
          <tbody>
            {shown.length === 0 && <tr><td colSpan={7} className="px-3 py-8 text-center text-zinc-600">INSUFFICIENT DATA — import a file or sync the live feed from Dashboard.</td></tr>}
            {shown.map((c) => (
              <tr key={c.ts} className="border-b border-zinc-900/50">
                <td className="px-3 py-1.5 text-zinc-400 whitespace-nowrap">{istT(c.ts)}</td>
                <td className="px-3 py-1.5">{c.open}</td><td className="px-3 py-1.5">{c.high}</td>
                <td className="px-3 py-1.5">{c.low}</td>
                <td className={`px-3 py-1.5 font-bold ${c.close >= c.open ? "text-[#22C55E]" : "text-red-400"}`}>{c.close}</td>
                <td className="px-3 py-1.5 text-zinc-500">{c.volume ?? "—"}</td>
                <td className="px-3 py-1.5 text-zinc-600">{c.source}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="border border-zinc-800 bg-[#0a0a0a]">
          <div className="px-4 py-3 border-b border-zinc-800 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
            Option contracts · {sym} ({contracts?.items?.length ?? 0})
          </div>
          <div className="max-h-80 overflow-y-auto">
            {(!contracts || contracts.items.length === 0) && <div className="px-4 py-8 text-center text-zinc-600 text-xs font-mono">No option contracts imported yet.</div>}
            {(contracts?.items || []).map((c) => (
              <button key={c.contract_id} onClick={() => setCid(c.contract_id)}
                      className={`w-full text-left px-4 py-2 text-xs font-mono border-b border-zinc-900/50 hover:bg-zinc-900/40 ${cid === c.contract_id ? "bg-zinc-900/60 text-white" : "text-zinc-400"}`}
                      data-testid={`ql-contract-${c.contract_id}`}>
                {c.expiry} · {c.strike} {c.option_type}
              </button>
            ))}
          </div>
        </div>
        <div className="border border-zinc-800 bg-[#0a0a0a]">
          <div className="px-4 py-3 border-b border-zinc-800 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
            Contract candles {cid && <span className="text-white normal-case">· {cid}</span>}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {!optCandles && <div className="px-4 py-8 text-center text-zinc-600 text-xs font-mono">Select a contract.</div>}
            {optCandles && (
              <table className="w-full text-xs font-mono">
                <thead><tr className="text-zinc-500 text-[10px] uppercase border-b border-zinc-900">
                  {["Time (IST)", "O", "H", "L", "C", "Vol", "OI", "IV"].map((h) => <th key={h} className="px-2 py-2 text-left">{h}</th>)}
                </tr></thead>
                <tbody>
                  {optCandles.items.slice(-100).reverse().map((c) => (
                    <tr key={c.ts} className="border-b border-zinc-900/50">
                      <td className="px-2 py-1 text-zinc-400 whitespace-nowrap">{istT(c.ts)}</td>
                      <td className="px-2 py-1">{c.open}</td><td className="px-2 py-1">{c.high}</td>
                      <td className="px-2 py-1">{c.low}</td><td className="px-2 py-1 font-bold">{c.close}</td>
                      <td className="px-2 py-1 text-zinc-500">{c.volume ?? "—"}</td>
                      <td className="px-2 py-1 text-zinc-500">{c.oi ?? "—"}</td>
                      <td className="px-2 py-1 text-zinc-500">{c.iv ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QLMarketData;
